import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext,
} from "vue-router";
import { isAuthenticated, getUserRole } from "@/services/authService";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: () => import("../views/customer/ProductListView.vue"),
    meta: {
      title: "物販一覧",
    },
  },
  {
    path: "/cart",
    name: "cart",
    component: () => import("../views/customer/CartView.vue"),
    meta: {
      title: "カート・注文確認",
    },
  },
  {
    path: "/complete/:orderId",
    name: "complete",
    component: () => import("../views/customer/OrderCompleteView.vue"),
    meta: {
      title: "注文完了",
    },
  },
  {
    path: "/auth/callback",
    name: "auth-callback",
    component: () => import("../views/customer/AuthCallbackView.vue"),
    meta: {
      title: "ログイン処理中",
    },
  },
  {
    path: "/mypage",
    name: "mypage",
    component: () => import("../views/customer/MyPageView.vue"),
    meta: {
      title: "マイページ",
      requiresCustomerAuth: true, // 顧客認証が必要
    },
  },
  {
    path: "/mypage/orders/:orderId",
    name: "mypage-order-detail",
    component: () => import("../views/customer/OrderDetailView.vue"),
    meta: {
      title: "注文詳細",
      requiresCustomerAuth: true, // 顧客認証が必要
    },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("../views/PrivacyPolicyView.vue"),
    meta: {
      title: "プライバシーポリシー",
    },
  },
  // スタッフ用ページ
  {
    path: "/backyard",
    name: "backyard",
    component: () => import("../views/backyard/BackyardView.vue"),
    meta: {
      title: "注文管理",
      requiresAuth: true,
      requiresRole: ["admin", "staff"], // adminとstaffのみアクセス可能
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/orders/:orderId",
    name: "backyard-order-detail",
    component: () => import("../views/backyard/OrderDetailView.vue"),
    meta: {
      title: "注文詳細",
      requiresAuth: true,
      requiresRole: ["admin", "staff"],
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/callback",
    name: "backyard-callback",
    component: () => import("../views/backyard/AuthCallbackView.vue"),
    meta: {
      title: "ログイン処理中",
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/send-order-confirmation-link",
    name: "backyard-send-order-confirmation-link",
    component: () =>
      import("../views/backyard/SendOrderConfirmationLinkView.vue"),
    meta: {
      title: "既存注文登録",
      requiresAuth: true,
      requiresRole: ["admin", "staff"],
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/confirm-existing-order/:token",
    name: "confirm-existing-order",
    component: () => import("../views/customer/ConfirmExistingOrderView.vue"),
    meta: {
      title: "注文内容の確認",
    },
  },
  // 404ページ
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/NotFoundView.vue"),
    meta: {
      title: "ページが見つかりません",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    savedPosition: { left: number; top: number } | null
  ) {
    // 常にページトップにスクロール
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// 認証チェックとページタイトルの設定
router.beforeEach(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    // ページタイトルの設定
    const title = to.meta.title as string;
    if (title) {
      document.title = `${title} | G.A.I.A 通販サイト`;
    } else {
      document.title = "G.A.I.A 通販サイト";
    }

    // robotsメタタグの設定
    const robots = to.meta.robots as string;
    let robotsMetaTag = document.querySelector('meta[name="robots"]');

    if (robots) {
      // robotsメタタグが存在しない場合は作成
      if (!robotsMetaTag) {
        robotsMetaTag = document.createElement("meta");
        robotsMetaTag.setAttribute("name", "robots");
        document.head.appendChild(robotsMetaTag);
      }
      robotsMetaTag.setAttribute("content", robots);
    } else if (robotsMetaTag) {
      // robotsメタタグが不要な場合は削除
      robotsMetaTag.remove();
    }

    // 顧客認証が必要なページの場合
    if (to.meta.requiresCustomerAuth) {
      const { isCustomerTokenValid, customerLogin, clearCustomerTokens } =
        await import("@/services/customerAuthService");
      if (!isCustomerTokenValid()) {
        // トークンクリア
        clearCustomerTokens();

        // ダイアログを表示せずに直接ログイン画面に誘導
        customerLogin();
        return;
      }
    }

    // スタッフ認証が必要なページの場合
    if (to.meta.requiresAuth) {
      if (!isAuthenticated()) {
        // 未認証の場合、認証サービスのloginを呼ぶ
        import("@/services/authService").then((authService) => {
          authService.login();
        });
        return;
      }

      // ロールチェック
      const requiresRole = to.meta.requiresRole as string[] | undefined;
      if (requiresRole && requiresRole.length > 0) {
        const userRole = getUserRole();
        if (!userRole || !requiresRole.includes(userRole)) {
          // 権限がない場合、ホームページにリダイレクト
          const { useDialogStore } = await import("@/store/dialog");
          const dialogStore = useDialogStore();
          await dialogStore.showAlert(
            `このページにアクセスする権限がありません。\n必要な権限: ${requiresRole.join(
              " または "
            )}\nあなたの権限: ${userRole || "なし"}`,
            "アクセス権限エラー"
          );
          next({ name: "home" });
          return;
        }
      }
    }

    next();
  }
);

export default router;
